import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import {
    PronunciationAnalysisResponse,
    GPTFeedbackResponse,
    AIAudioComparisonResponse,
    AvatarPracticeResponse,
} from '@/services/speaking/practiceService';

// === PRACTICE FLOW ACTIONS ===
export const startPracticeFlowRequest = createAction<{
    audio_blob: Blob;
    target_text: string;
    exercise_id?: string;
    feedback_tone?: 'playful' | 'serious' | 'explanatory' | 'encouraging';
    avatar_type?: '2d' | '3d';
}>('practice/startPracticeFlowRequest');

export const startPracticeFlowSuccess = createAction<{
    pronunciation_analysis: PronunciationAnalysisResponse;
    gpt_feedback: GPTFeedbackResponse;
    audio_comparison: AIAudioComparisonResponse;
    avatar_practice: AvatarPracticeResponse;
    vector_update: { message: string; updated_vector: any };
}>('practice/startPracticeFlowSuccess');

export const startPracticeFlowFailure = createAction<string>('practice/startPracticeFlowFailure');

// === INDIVIDUAL STEP ACTIONS ===
export const analyzePronunciationRequest = createAction<{
    audio_blob: Blob;
    target_text: string;
    content_type: string;
    exercise_id?: string;
}>('practice/analyzePronunciationRequest');

export const analyzePronunciationSuccess = createAction<PronunciationAnalysisResponse>(
    'practice/analyzePronunciationSuccess',
);
export const analyzePronunciationFailure = createAction<string>('practice/analyzePronunciationFailure');

export const generateFeedbackRequest = createAction<{
    pronunciation_errors: any[];
    overall_score: number;
    target_text: string;
    feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}>('practice/generateFeedbackRequest');

export const generateFeedbackSuccess = createAction<GPTFeedbackResponse>('practice/generateFeedbackSuccess');
export const generateFeedbackFailure = createAction<string>('practice/generateFeedbackFailure');

export const generateReferenceAudioRequest = createAction<{
    user_audio_url: string;
    target_text: string;
    target_phonemes: string[];
}>('practice/generateReferenceAudioRequest');

export const generateReferenceAudioSuccess = createAction<AIAudioComparisonResponse>(
    'practice/generateReferenceAudioSuccess',
);
export const generateReferenceAudioFailure = createAction<string>('practice/generateReferenceAudioFailure');

export const generateAvatarPracticeRequest = createAction<{
    target_text: string;
    problematic_phonemes: string[];
    user_level: string;
    avatar_type: '2d' | '3d';
    feedback_tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
}>('practice/generateAvatarPracticeRequest');

export const generateAvatarPracticeSuccess = createAction<AvatarPracticeResponse>(
    'practice/generateAvatarPracticeSuccess',
);
export const generateAvatarPracticeFailure = createAction<string>('practice/generateAvatarPracticeFailure');

// === SESSION MANAGEMENT ACTIONS ===
export const startPracticeSessionRequest = createAction<{ exercise_id?: string }>(
    'practice/startPracticeSessionRequest',
);
export const startPracticeSessionSuccess = createAction<{ session_id: string; started_at: string }>(
    'practice/startPracticeSessionSuccess',
);
export const startPracticeSessionFailure = createAction<string>('practice/startPracticeSessionFailure');

export const endPracticeSessionRequest = createAction<{ session_id: string; session_data: any }>(
    'practice/endPracticeSessionRequest',
);
export const endPracticeSessionSuccess = createAction<{ message: string; results: any }>(
    'practice/endPracticeSessionSuccess',
);
export const endPracticeSessionFailure = createAction<string>('practice/endPracticeSessionFailure');

// === RECORDING ACTIONS ===
export const setPracticeState = createAction<{
    isRecording?: boolean;
    recordingDuration?: number;
    hasRecording?: boolean;
}>('practice/setPracticeState');

export const setRecordingState = createAction<{
    isRecording: boolean;
    duration: number;
    audioBlob?: Blob;
}>('practice/setRecordingState');

export interface AudioState {
    userAudioUrl?: string;
    aiAudioUrl?: string;
    isUserAudioPlaying: boolean;
    isAiAudioPlaying: boolean;
    currentPlaybackMode: 'user' | 'ai' | 'both';
    duration: number;
    currentTime: number;
}

export interface WaveformData {
    peaks: number[];
    stressMarkers: Array<{
        start: number;
        end: number;
        intensity: 'primary' | 'secondary' | 'weak';
        color: string;
    }>;
    errorMarkers: Array<{
        start: number;
        end: number;
        severity: 'minor' | 'moderate' | 'major';
        phoneme: string;
        message: string;
    }>;
    phonemeSegments: Array<{
        start: number;
        end: number;
        phoneme: string;
        accuracy: number;
        isCorrect: boolean;
    }>;
}

export interface ConversationalFeedback {
    id: string;
    message: string;
    tone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
    timestamp: number;
    phonemeTargets?: string[];
    suggestions?: string[];
    encouragement?: string;
}

export interface SkillMapData {
    phonemeScores: Array<{
        phoneme: string;
        accuracy: number;
        totalPractice: number;
        trend: 'improving' | 'plateau' | 'declining';
        lastPracticed: string;
    }>;
    overallAccuracy: number;
    improvementAreas: string[];
    masteredPhonemes: string[];
    weeklyProgress: Array<{
        week: string;
        accuracy: number;
    }>;
}

export interface AvatarState {
    isActive: boolean;
    currentExpression: 'neutral' | 'encouraging' | 'thinking' | 'celebrating' | 'concerned';
    currentAnimation: 'idle' | 'speaking' | 'listening' | 'demonstrating';
    lastFeedback?: string;
    interactionMode: '2d' | '3d';
}

export interface ExerciseProgress {
    sessionId?: string;
    exerciseId?: string;
    currentAttempt: number;
    maxAttempts: number;
    attempts: Array<{
        id: string;
        score: number;
        feedback: ConversationalFeedback;
        waveformData: WaveformData;
        audioUrl: string;
    }>;
    isCompleted: boolean;
    overallScore: number;
}

export interface PracticeState {
    // Practice Flow Results
    currentFlow: {
        pronunciationAnalysis: PronunciationAnalysisResponse | null;
        gptFeedback: GPTFeedbackResponse | null;
        audioComparison: AIAudioComparisonResponse | null;
        avatarPractice: AvatarPracticeResponse | null;
        vectorUpdate: { message: string; updated_vector: any } | null;
    };

    // Session Management
    currentSession: {
        session_id: string | null;
        started_at: string | null;
        exercise_id: string | null;
    };

    // Recording State
    recording: {
        isRecording: boolean;
        duration: number;
        audioBlob: Blob | null;
        hasRecording: boolean;
    };

    // Audio Management
    audio: AudioState;

    // Waveform & Visual Feedback
    waveform: WaveformData | null;
    isWaveformLoading: boolean;

    // Conversational Feedback
    feedbackHistory: ConversationalFeedback[];
    selectedFeedbackTone: 'playful' | 'serious' | 'explanatory' | 'encouraging';
    isGeneratingFeedback: boolean;

    // Skill Map & Analytics
    skillMap: SkillMapData | null;
    isSkillMapLoading: boolean;

    // Avatar Mode
    avatar: AvatarState;

    // Exercise Progress
    currentExercise: ExerciseProgress;

    // UI State
    activeTab: 'practice' | 'analytics' | 'avatar';

    // Notifications
    recentAlerts: Array<{
        id: string;
        type: 'mispronunciation' | 'milestone' | 'streak';
        message: string;
        phonemes?: string[];
        timestamp: number;
        isRead: boolean;
    }>;

    // Loading States
    isLoadingPracticeFlow: boolean;
    isLoadingPronunciationAnalysis: boolean;
    isLoadingGptFeedback: boolean;
    isLoadingReferenceAudio: boolean;
    isLoadingAvatarPractice: boolean;
    isLoadingSession: boolean;
    loading: boolean;

    // Error States
    practiceFlowError: string | null;
    pronunciationAnalysisError: string | null;
    gptFeedbackError: string | null;
    referenceAudioError: string | null;
    avatarPracticeError: string | null;
    sessionError: string | null;
    error: string | null;
}

const initialState: PracticeState = {
    currentFlow: {
        pronunciationAnalysis: null,
        gptFeedback: null,
        audioComparison: null,
        avatarPractice: null,
        vectorUpdate: null,
    },

    currentSession: {
        session_id: null,
        started_at: null,
        exercise_id: null,
    },

    recording: {
        isRecording: false,
        duration: 0,
        audioBlob: null,
        hasRecording: false,
    },

    audio: {
        isUserAudioPlaying: false,
        isAiAudioPlaying: false,
        currentPlaybackMode: 'user',
        duration: 0,
        currentTime: 0,
    },

    waveform: null,
    isWaveformLoading: false,

    feedbackHistory: [],
    selectedFeedbackTone: 'encouraging',
    isGeneratingFeedback: false,

    skillMap: null,
    isSkillMapLoading: false,

    avatar: {
        isActive: false,
        currentExpression: 'neutral',
        currentAnimation: 'idle',
        interactionMode: '2d',
    },

    currentExercise: {
        currentAttempt: 1,
        maxAttempts: 3,
        attempts: [],
        isCompleted: false,
        overallScore: 0,
    },

    activeTab: 'practice',

    recentAlerts: [],

    isLoadingPracticeFlow: false,
    isLoadingPronunciationAnalysis: false,
    isLoadingGptFeedback: false,
    isLoadingReferenceAudio: false,
    isLoadingAvatarPractice: false,
    isLoadingSession: false,
    loading: false,

    practiceFlowError: null,
    pronunciationAnalysisError: null,
    gptFeedbackError: null,
    referenceAudioError: null,
    avatarPracticeError: null,
    sessionError: null,
    error: null,
};

const practiceSlice = createSlice({
    name: 'speakingPractice',
    initialState,
    reducers: {
        // Recording Controls
        startRecording: (state) => {
            state.recording.isRecording = true;
        },

        stopRecording: (state, action: PayloadAction<Blob>) => {
            state.recording.isRecording = false;
            state.recording.audioBlob = action.payload;
            state.recording.hasRecording = true;
        },

        // Audio Controls
        setAudioUrls: (state, action: PayloadAction<{ userAudio?: string; aiAudio?: string }>) => {
            if (action.payload.userAudio) {
                state.audio.userAudioUrl = action.payload.userAudio;
            }
            if (action.payload.aiAudio) {
                state.audio.aiAudioUrl = action.payload.aiAudio;
            }
        },

        toggleAudioPlayback: (state, action: PayloadAction<'user' | 'ai'>) => {
            const audioType = action.payload;
            if (audioType === 'user') {
                state.audio.isUserAudioPlaying = !state.audio.isUserAudioPlaying;
                state.audio.isAiAudioPlaying = false;
            } else {
                state.audio.isAiAudioPlaying = !state.audio.isAiAudioPlaying;
                state.audio.isUserAudioPlaying = false;
            }
            state.audio.currentPlaybackMode = audioType;
        },

        updateAudioTime: (state, action: PayloadAction<{ currentTime: number; duration: number }>) => {
            state.audio.currentTime = action.payload.currentTime;
            state.audio.duration = action.payload.duration;
        },

        // Waveform Controls
        setWaveformData: (state, action: PayloadAction<WaveformData>) => {
            state.waveform = action.payload;
            state.isWaveformLoading = false;
        },

        setWaveformLoading: (state, action: PayloadAction<boolean>) => {
            state.isWaveformLoading = action.payload;
        },

        // Feedback Controls
        addFeedback: (state, action: PayloadAction<ConversationalFeedback>) => {
            state.feedbackHistory.push(action.payload);
        },

        setGeneratingFeedback: (state, action: PayloadAction<boolean>) => {
            state.isGeneratingFeedback = action.payload;
        },

        // UI Controls
        setActiveTab: (state, action: PayloadAction<'practice' | 'analytics' | 'avatar'>) => {
            state.activeTab = action.payload;
        },

        setFeedbackTone: (state, action: PayloadAction<'playful' | 'serious' | 'explanatory' | 'encouraging'>) => {
            state.selectedFeedbackTone = action.payload;
        },

        // Avatar Controls
        updateAvatarState: (state, action: PayloadAction<Partial<AvatarState>>) => {
            state.avatar = { ...state.avatar, ...action.payload };
        },

        // Exercise Progress
        startNewExercise: (
            state,
            action: PayloadAction<{ exerciseId: string; sessionId: string; maxAttempts: number }>,
        ) => {
            state.currentExercise = {
                exerciseId: action.payload.exerciseId,
                sessionId: action.payload.sessionId,
                currentAttempt: 1,
                maxAttempts: action.payload.maxAttempts,
                attempts: [],
                isCompleted: false,
                overallScore: 0,
            };
        },

        addExerciseAttempt: (
            state,
            action: PayloadAction<{
                id: string;
                score: number;
                feedback: ConversationalFeedback;
                waveformData: WaveformData;
                audioUrl: string;
            }>,
        ) => {
            state.currentExercise.attempts.push(action.payload);
            state.currentExercise.currentAttempt++;

            // Check if exercise is completed
            if (state.currentExercise.currentAttempt > state.currentExercise.maxAttempts) {
                state.currentExercise.isCompleted = true;
            }

            // Calculate overall score
            const scores = state.currentExercise.attempts.map((a) => a.score);
            state.currentExercise.overallScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        },

        // Notifications
        addAlert: (
            state,
            action: PayloadAction<{
                id: string;
                type: 'mispronunciation' | 'milestone' | 'streak';
                message: string;
                phonemes?: string[];
            }>,
        ) => {
            state.recentAlerts.unshift({
                ...action.payload,
                timestamp: Date.now(),
                isRead: false,
            });
        },

        markAlertAsRead: (state, action: PayloadAction<string>) => {
            const alert = state.recentAlerts.find((a) => a.id === action.payload);
            if (alert) {
                alert.isRead = true;
            }
        },

        // Global Controls
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        resetPracticeState: (state) => {
            return { ...initialState };
        },

        // Reset state
        resetPracticeFlow: (state) => {
            state.currentFlow = {
                pronunciationAnalysis: null,
                gptFeedback: null,
                audioComparison: null,
                avatarPractice: null,
                vectorUpdate: null,
            };
            state.practiceFlowError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // === PRACTICE FLOW ===
            .addCase(startPracticeFlowRequest, (state) => {
                state.isLoadingPracticeFlow = true;
                state.practiceFlowError = null;
            })
            .addCase(startPracticeFlowSuccess, (state, action) => {
                state.isLoadingPracticeFlow = false;
                state.currentFlow = {
                    pronunciationAnalysis: action.payload.pronunciation_analysis,
                    gptFeedback: action.payload.gpt_feedback,
                    audioComparison: action.payload.audio_comparison,
                    avatarPractice: action.payload.avatar_practice,
                    vectorUpdate: action.payload.vector_update,
                };
            })
            .addCase(startPracticeFlowFailure, (state, action) => {
                state.isLoadingPracticeFlow = false;
                state.practiceFlowError = action.payload;
            })

            // === PRONUNCIATION ANALYSIS ===
            .addCase(analyzePronunciationRequest, (state) => {
                state.isLoadingPronunciationAnalysis = true;
                state.pronunciationAnalysisError = null;
            })
            .addCase(analyzePronunciationSuccess, (state, action) => {
                state.isLoadingPronunciationAnalysis = false;
                state.currentFlow.pronunciationAnalysis = action.payload;
            })
            .addCase(analyzePronunciationFailure, (state, action) => {
                state.isLoadingPronunciationAnalysis = false;
                state.pronunciationAnalysisError = action.payload;
            })

            // === GPT FEEDBACK ===
            .addCase(generateFeedbackRequest, (state) => {
                state.isLoadingGptFeedback = true;
                state.gptFeedbackError = null;
            })
            .addCase(generateFeedbackSuccess, (state, action) => {
                state.isLoadingGptFeedback = false;
                state.currentFlow.gptFeedback = action.payload;
            })
            .addCase(generateFeedbackFailure, (state, action) => {
                state.isLoadingGptFeedback = false;
                state.gptFeedbackError = action.payload;
            })

            // === REFERENCE AUDIO ===
            .addCase(generateReferenceAudioRequest, (state) => {
                state.isLoadingReferenceAudio = true;
                state.referenceAudioError = null;
            })
            .addCase(generateReferenceAudioSuccess, (state, action) => {
                state.isLoadingReferenceAudio = false;
                state.currentFlow.audioComparison = action.payload;
            })
            .addCase(generateReferenceAudioFailure, (state, action) => {
                state.isLoadingReferenceAudio = false;
                state.referenceAudioError = action.payload;
            })

            // === AVATAR PRACTICE ===
            .addCase(generateAvatarPracticeRequest, (state) => {
                state.isLoadingAvatarPractice = true;
                state.avatarPracticeError = null;
            })
            .addCase(generateAvatarPracticeSuccess, (state, action) => {
                state.isLoadingAvatarPractice = false;
                state.currentFlow.avatarPractice = action.payload;
            })
            .addCase(generateAvatarPracticeFailure, (state, action) => {
                state.isLoadingAvatarPractice = false;
                state.avatarPracticeError = action.payload;
            })

            // === SESSION MANAGEMENT ===
            .addCase(startPracticeSessionRequest, (state) => {
                state.isLoadingSession = true;
                state.sessionError = null;
            })
            .addCase(startPracticeSessionSuccess, (state, action) => {
                state.isLoadingSession = false;
                state.currentSession = {
                    session_id: action.payload.session_id,
                    started_at: action.payload.started_at,
                    exercise_id: null,
                };
            })
            .addCase(startPracticeSessionFailure, (state, action) => {
                state.isLoadingSession = false;
                state.sessionError = action.payload;
            })

            .addCase(endPracticeSessionRequest, (state) => {
                state.isLoadingSession = true;
                state.sessionError = null;
            })
            .addCase(endPracticeSessionSuccess, (state, action) => {
                state.isLoadingSession = false;
                // Could store session results here
            })
            .addCase(endPracticeSessionFailure, (state, action) => {
                state.isLoadingSession = false;
                state.sessionError = action.payload;
            })

            // === RECORDING STATE ===
            .addCase(setPracticeState, (state, action) => {
                if (action.payload.isRecording !== undefined) {
                    state.recording.isRecording = action.payload.isRecording;
                }
                if (action.payload.recordingDuration !== undefined) {
                    state.recording.duration = action.payload.recordingDuration;
                }
                if (action.payload.hasRecording !== undefined) {
                    state.recording.hasRecording = action.payload.hasRecording;
                }
            })

            .addCase(setRecordingState, (state, action) => {
                state.recording = {
                    ...state.recording,
                    isRecording: action.payload.isRecording,
                    duration: action.payload.duration,
                    audioBlob: action.payload.audioBlob || state.recording.audioBlob,
                };
            });
    },
});

export const {
    // Recording actions
    startRecording,
    stopRecording,

    // Audio actions
    setAudioUrls,
    toggleAudioPlayback,
    updateAudioTime,

    // Waveform actions
    setWaveformData,
    setWaveformLoading,

    // Feedback actions
    addFeedback,
    setGeneratingFeedback,
    setFeedbackTone,

    // Avatar actions
    updateAvatarState,

    // Exercise actions
    startNewExercise,
    addExerciseAttempt,

    // UI actions
    setActiveTab,

    // Notification actions
    addAlert,
    markAlertAsRead,

    // Global actions
    setLoading,
    setError,
    resetPracticeState,
    resetPracticeFlow,
} = practiceSlice.actions;

export default practiceSlice.reducer;
